import { useState, useEffect, useRef, useMemo } from 'react'
import {
  Box,
  Stack,
  VStack,
  HStack,
  Text,
  Avatar,
  Badge,
  Button,
  IconButton,
  Input,
  Checkbox,
  Spinner,
  FormControl,
  FormLabel,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { AddIcon, DeleteIcon } from '@chakra-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import { glassCard, inputStyle } from '../styles'
import { apiFetch } from '../utils/apiFetch'

interface Member {
  _id: string
  email: string
  name?: string
  isAdmin: boolean
  isActive: boolean
}

const emptyForm = { email: '', isAdmin: false }

export default function Members() {
  const [user] = useState(() => {
    const data = localStorage.getItem('user')
    return data ? JSON.parse(data) : null
  })
  const [members, setMembers] = useState<Member[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  // Shared confirmation dialog state
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const isAdmin: boolean = user?.isAdmin ?? false

  const filteredMembers = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return members
    return members.filter(m =>
      (m.name ?? '').toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
    )
  }, [members, search])

  useEffect(() => {
    apiFetch('/api/users')
      .then(res => res.json())
      .then(data => {
        setMembers(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const confirm = (message: string, action: () => void) => {
    setConfirmMessage(message)
    setPendingAction(() => action)
    onConfirmOpen()
  }

  const handleConfirm = () => {
    pendingAction?.()
    onConfirmClose()
  }

  const handleAdd = async () => {
    if (!form.email) return
    setSaving(true)
    const res = await apiFetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
    if (res.ok) {
      const created = await res.json()
      setMembers(prev => [...prev, created].sort((a, b) => a.email.localeCompare(b.email)))
      setForm(emptyForm)
      setShowForm(false)
    }
    setSaving(false)
  }

  const handleToggleActive = async (m: Member) => {
    const action = m.isActive ? 'Deactivate' : 'Reactivate'
    const detail = m.isActive ? "They won't be able to log in." : 'They will be able to log in again.'
    confirm(`${action} ${m.name ?? m.email}? ${detail}`, async () => {
      const res = await apiFetch(`/api/users/${m._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !m.isActive }),
      })
      if (res.ok) {
        const updated = await res.json()
        setMembers(prev => prev.map(member => (member._id === updated._id ? updated : member)))
      }
    })
  }

  const handleDelete = (m: Member) => {
    confirm(`Delete ${m.name ?? m.email}? This cannot be undone.`, async () => {
      const res = await apiFetch(`/api/users/${m._id}`, { method: 'DELETE' })
      if (res.ok) {
        setMembers(prev => prev.filter(member => member._id !== m._id))
      }
    })
  }

  const handleMakeAdmin = (m: Member) => {
    confirm(`Make ${m.name ?? m.email} an admin? This cannot be undone.`, async () => {
      const res = await apiFetch(`/api/users/${m._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isAdmin: true }),
      })
      if (res.ok) {
        const updated = await res.json()
        setMembers(prev => prev.map(member => (member._id === updated._id ? updated : member)))
      }
    })
  }

  return (
    <Box minH="100vh" position="relative" p={{ base: 4, md: 8 }} pt={{ base: 16, md: 20 }}>

      <Box maxW="700px" mx="auto">
        <VStack spacing={6} align="stretch">

          <HStack justifyContent="space-between" alignItems="center">
            <Text fontSize="2xl" fontWeight="bold" color="white">👥 Members</Text>
            {isAdmin && !showForm && (
              <Button
                leftIcon={<AddIcon />}
                size="sm"
                bg="whiteAlpha.200"
                color="white"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ bg: 'whiteAlpha.300' }}
                onClick={() => setShowForm(true)}
              >
                Add Member
              </Button>
            )}
          </HStack>

          <Input
            placeholder="Search members..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            {...inputStyle}
          />

          {isAdmin && showForm && (
            <Box {...glassCard} borderRadius="xl" p={{ base: 4, md: 6 }}>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="semibold" color="white">New Member</Text>
                <FormControl>
                  <FormLabel color="whiteAlpha.700" fontSize="sm">Email</FormLabel>
                  <Input
                    placeholder="user@example.com"
                    value={form.email}
                    onChange={e => setForm(form => ({ ...form, email: e.target.value }))}
                    color="white"
                    borderColor="whiteAlpha.300"
                    _placeholder={{ color: 'whiteAlpha.400' }}
                  />
                </FormControl>
                <Checkbox
                  isChecked={form.isAdmin}
                  onChange={e => setForm(form => ({ ...form, isAdmin: e.target.checked }))}
                  colorScheme="orange"
                  color="whiteAlpha.700"
                >
                  Admin
                </Checkbox>
                <HStack justify="flex-end" spacing={3}>
                  <Button
                    size="sm"
                    variant="ghost"
                    color="whiteAlpha.700"
                    onClick={() => { setShowForm(false); setForm(emptyForm) }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" colorScheme="orange" isLoading={saving} onClick={handleAdd} isDisabled={!form.email}>
                    Add Member
                  </Button>
                </HStack>
              </VStack>
            </Box>
          )}

          {loading ? (
            <Spinner color="white" />
          ) : (
            <VStack spacing={3} align="stretch">
              {filteredMembers.map(member => (
                <RouterLink key={member._id} to={`/members/${member._id}`} style={{ textDecoration: 'none' }}>
                  <Box
                    {...glassCard}
                    borderColor={member.isActive ? 'whiteAlpha.200' : 'red.800'}
                    borderRadius="lg"
                    p={4}
                    opacity={member.isActive ? 1 : 0.6}
                    cursor="pointer"
                    _hover={{ bg: 'whiteAlpha.200' }}
                  >
                    <Stack
                      direction={{ base: 'column', md: 'row' }}
                      justify="space-between"
                      align={{ base: 'stretch', md: 'center' }}
                      spacing={2}
                    >
                      <HStack spacing={3} flex={1}>
                        <Avatar size="sm" name={member.name ?? member.email} border="2px solid" borderColor="whiteAlpha.300" />
                        <VStack align="start" spacing={1}>
                          <Text color="white" fontSize="sm" fontWeight="semibold">{member.name ?? member.email}</Text>
                          {member.name && <Text color="whiteAlpha.500" fontSize="xs">{member.email}</Text>}
                          <HStack spacing={2}>
                            {member.isAdmin && <Badge colorScheme="orange" fontSize="xs">Admin</Badge>}
                            {!member.isActive && <Badge colorScheme="red" fontSize="xs">Inactive</Badge>}
                          </HStack>
                        </VStack>
                      </HStack>
                      {isAdmin && !member.isAdmin && (
                        <HStack spacing={1} flexWrap="wrap" flexShrink={0}>
                          <Button
                            size="xs"
                            variant="ghost"
                            color="purple.300"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            onClick={e => { e.preventDefault(); handleMakeAdmin(member) }}
                          >
                            Make Admin
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            color={member.isActive ? 'yellow.300' : 'green.300'}
                            _hover={{ bg: 'whiteAlpha.200' }}
                            onClick={e => { e.preventDefault(); handleToggleActive(member) }}
                          >
                            {member.isActive ? 'Deactivate' : 'Reactivate'}
                          </Button>
                          <IconButton
                            aria-label="Remove member"
                            icon={<DeleteIcon />}
                            size="xs"
                            variant="ghost"
                            color="red.300"
                            _hover={{ bg: 'whiteAlpha.200', color: 'red.200' }}
                            onClick={e => { e.preventDefault(); handleDelete(member) }}
                          />
                        </HStack>
                      )}
                    </Stack>
                  </Box>
                </RouterLink>
              ))}
            </VStack>
          )}

        </VStack>
      </Box>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" borderColor="whiteAlpha.200" border="1px solid">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">Confirm</AlertDialogHeader>
            <AlertDialogBody color="whiteAlpha.800">{confirmMessage}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" color="whiteAlpha.700" onClick={onConfirmClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleConfirm} ml={3}>Confirm</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
